import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { horizontalSlide } from '../../animations';
import {
  AvailabilityUtils,
  Shift,
  ContractorAvailabilityException,
  ContractorAvailabilityDto,
  CreateContractorAvailabilityException,
  JS_DAY_TO_DAY_OF_WEEK,
  CONTRACTOR_AVAILABILITY_DATA_SERVICE
} from '@umss/shared/data-access';
import { UmssCardHeaderComponent } from '../card/card-header/card-header.component';
import { Observable, Subject, forkJoin, switchMap, tap } from 'rxjs';
import {
  UmssCalendarComponent,
  UmssCalendarMarkerType,
  UmssCalendarSize
} from '../calendar/calendar.component';
import { UmssBadgeComponent } from '../badge/badge.component';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssButtonComponent } from '../button/button.component';
import { AddAvailabilityExceptionDialogComponent } from './add-availability-exception-dialog/add-availability-exception-dialog.component';
import { DatePipe } from '@angular/common';
import { ConfirmationService } from '../../services/confirmation.service';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';
import dayjs from 'dayjs';

export interface SelectedDateInfo {
  date: Date;
  availabilitySlots: { startTime: string; endTime: string }[];
  isUnavailableException: boolean;
  isAvailableException: boolean;
  exceptionId: number | null;
  exceptionReason: string | null;
  shifts: Shift[];
}

/**
 * Maps JavaScript Date.getDay() numbers to DayOfWeek string enum values.
 */

@Component({
  selector: 'umss-contractor-availability-card',
  standalone: true,
  imports: [
    UmssCardHeaderComponent,
    UmssCalendarComponent,
    UmssBadgeComponent,
    UmssIconComponent,
    UmssButtonComponent,
    AddAvailabilityExceptionDialogComponent,
    UmssTooltipDirective,
    DatePipe
  ],
  templateUrl: './contractor-availability-card.component.html',
  styleUrls: ['./contractor-availability-card.component.scss'],
  animations: [horizontalSlide]
})
export class ContractorAvailabilityCardComponent implements OnInit {
  contractorId = input<string>();
  facilityId = input<string>();
  allowSelectAnyDate = input<boolean>(false);
  readonly = input<boolean>(false);
  title = input<string>('Monthly Availability');

  shiftClick = output<Shift>();
  disabledTooltipMessage = input<string | null>('No availability on this date');

  private readonly availabilityDataService = inject(
    CONTRACTOR_AVAILABILITY_DATA_SERVICE
  );
  private destroyRef = inject(DestroyRef);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);

  availabilityDates: Date[] = [];
  unavailableDates: Date[] = [];
  bookedDates: Date[] = [];
  clickableUnavailableDates: Date[] = [];
  currentDate = new Date();
  loading = true;

  readonly UmssCalendarSize = UmssCalendarSize;
  readonly UmssCalendarMarkerType = UmssCalendarMarkerType;

  /** Subject that triggers data loading; switchMap cancels previous in-flight requests */
  private loadTrigger$ = new Subject<Date>();

  availabilityExceptions: ContractorAvailabilityException[] = [];

  /** Raw data from the last fetch, used to compute selected date details */
  private rawAvailability: ContractorAvailabilityDto[] = [];
  private rawShifts: Shift[] = [];

  selectedDate = signal<Date | null>(null);
  showExceptionDialog = signal<boolean>(false);
  selectedDateAnimIndex = signal<number>(0);

  isSelectedDateInPast = computed(() => {
    const selected = this.selectedDate();
    if (!selected) return false;
    return dayjs(selected).isBefore(dayjs(), 'day');
  });

  selectedDateInfo = computed<SelectedDateInfo | null>(() => {
    const date = this.selectedDate();
    if (!date) return null;

    const dateKey = this.toDateKey(date);
    const jsDayOfWeek = date.getDay();
    const dayOfWeek = JS_DAY_TO_DAY_OF_WEEK[jsDayOfWeek];

    // Check for exception on this date
    const exception = this.availabilityExceptions.find(
      (e) => e.exceptionDate.slice(0, 10) === dateKey
    );

    let availabilitySlots: { startTime: string; endTime: string }[] = [];
    let isUnavailableException = false;
    let isAvailableException = false;
    let exceptionId: number | null = null;
    let exceptionReason: string | null = null;

    if (exception) {
      exceptionId = exception.id;
      if (exception.isUnavailable) {
        isUnavailableException = true;
        exceptionReason = exception.reason;
      } else {
        isAvailableException = true;
        // Exception adds availability — use exception's time slot
        if (exception.startTime && exception.endTime) {
          availabilitySlots = [
            { startTime: exception.startTime, endTime: exception.endTime }
          ];
        }
        exceptionReason = exception.reason;
      }
    } else {
      // No exception — use weekly schedule
      availabilitySlots = this.rawAvailability
        .filter((a) => a.dayOfWeek === dayOfWeek && a.isActive !== false)
        .map((a) => ({ startTime: a.startTime, endTime: a.endTime }));
    }

    // Shifts on this date
    const shifts = this.rawShifts.filter(
      (s) => s.shiftDate.slice(0, 10) === dateKey
    );

    return {
      date,
      availabilitySlots,
      isUnavailableException,
      isAvailableException,
      exceptionId,
      exceptionReason,
      shifts
    };
  });

  selectedDateInfoEntries = computed<SelectedDateInfo[]>(() => {
    const info = this.selectedDateInfo();
    return info ? [info] : [];
  });

  ngOnInit() {
    this.loadTrigger$
      .pipe(
        tap(() => (this.loading = true)),
        switchMap((date) => this.fetchMonthData(date)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (data) => {
          this.applyMonthData(data);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load contractor availability:', error);
          this.loading = false;
          this.cdr.markForCheck();
        }
      });

    this.loadTrigger$.next(this.currentDate);
  }

  private fetchMonthData(date: Date): Observable<{
    availability: ContractorAvailabilityDto[];
    shifts: Shift[];
    exceptions: ContractorAvailabilityException[];
  }> {
    const contractorId = this.contractorId();

    const year = dayjs(date).year();
    const month = dayjs(date).month();

    const startDate = dayjs(new Date(year, month, 1)).format('YYYY-MM-DD');
    const endDate = dayjs(new Date(year, month + 1, 0)).format('YYYY-MM-DD');

    return forkJoin({
      availability: this.availabilityDataService.getAvailability(contractorId),
      shifts: this.availabilityDataService.getShiftsForMonth(
        year,
        month,
        contractorId
      ),
      exceptions: this.availabilityDataService.getAvailabilityExceptions(
        contractorId,
        { startDate, endDate }
      )
    });
  }

  private applyMonthData({
    availability,
    shifts,
    exceptions
  }: {
    availability: ContractorAvailabilityDto[];
    shifts: Shift[];
    exceptions: ContractorAvailabilityException[];
  }): void {
    const year = dayjs(this.currentDate).year();
    const month = dayjs(this.currentDate).month();

    // Step 1: Compute base dates from weekly availability pattern
    const weeklyAvailable = AvailabilityUtils.convertToCalendarDates(
      availability,
      year,
      month
    );

    const weeklyUnavailable = AvailabilityUtils.convertToUnavailableDates(
      availability,
      year,
      month
    );

    // Step 2: Apply exceptions (isUnavailable=true blocks, isUnavailable=false adds)
    const adjusted = AvailabilityUtils.applyExceptions(
      weeklyAvailable,
      weeklyUnavailable,
      exceptions
    );

    this.availabilityDates = adjusted.availableDates;
    this.unavailableDates = adjusted.unavailableDates;

    this.bookedDates = shifts.map((shift) => {
      const [y, m, d] = shift.shiftDate.split('-').map(Number);
      return new Date(y, m - 1, d);
    });

    // Dates that are unavailable but have a booked shift must stay clickable
    const unavailableKeys = new Set(
      adjusted.unavailableDates.map((d) => this.toDateKey(d))
    );
    this.clickableUnavailableDates = this.bookedDates.filter((d) =>
      unavailableKeys.has(this.toDateKey(d))
    );

    this.availabilityExceptions = exceptions;
    this.rawAvailability = availability;
    this.rawShifts = shifts;
    this.loading = false;

    // Re-trigger selectedDateInfo if a date was already selected
    const current = this.selectedDate();
    if (current) {
      this.selectedDate.set(null);
      this.selectedDate.set(current);
    }
  }

  refresh(): void {
    this.loadTrigger$.next(this.currentDate);
  }

  onMonthChange(date: Date) {
    this.currentDate = date;
    this.selectedDate.set(null);
    this.loadTrigger$.next(date);
  }

  openAddExceptionDialog(): void {
    this.showExceptionDialog.set(true);
  }

  async deleteException(exceptionId: number): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      title: 'Delete Exception',
      message: 'Are you sure you want to delete this exception?',
      type: 'Error'
    });
    if (!confirmed) return;

    this.availabilityDataService
      .deleteAvailabilityException(exceptionId, this.contractorId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.selectedDate.set(null);
          this.loadTrigger$.next(this.currentDate);
        },
        error: (err) =>
          console.error('Failed to delete availability exception:', err)
      });
  }

  onExceptionDialogClose(
    result: CreateContractorAvailabilityException | undefined
  ): void {
    this.showExceptionDialog.set(false);
    if (result) {
      this.availabilityDataService
        .createAvailabilityException(result, this.contractorId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.loadTrigger$.next(this.currentDate),
          error: (err) =>
            console.error('Failed to create availability exception:', err)
        });
    }
  }

  onDateClick(date: Date) {
    const prev = this.selectedDate();

    if (prev) {
      if (date.getTime() > prev.getTime()) {
        this.selectedDateAnimIndex.update((i) => i + 1);
      } else if (date.getTime() < prev.getTime()) {
        this.selectedDateAnimIndex.update((i) => i - 1);
      }
    } else {
      this.selectedDateAnimIndex.update((i) => i + 1);
    }

    this.selectedDate.set(date);
  }

  onShiftClick(shift: Shift) {
    this.shiftClick.emit(shift);
  }

  formatTime(time: string): string {
    return AvailabilityUtils.formatTime(time, '24h');
  }

  isAllDay(slot: { startTime: string; endTime: string }): boolean {
    const start = slot.startTime.replace(/:\d{2}$/, '');
    const end = slot.endTime.replace(/:\d{2}$/, '');
    return start === '00:00' && (end === '23:59' || end === '24:00');
  }

  private toDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
